// Copyright 2025, University of Maryland, All Rights Reserved
import type { AxiosInstance, AxiosResponse } from 'axios'

/**
 * Node-specific API client wrapper.
 *
 * This class wraps the main ApiClient's axios instance to provide
 * a consistent interface for node-specific API calls.
 *
 * All authentication, error handling, and header management is inherited
 * from the main ApiClient, eliminating duplicate configuration.
 */
export class NodeApiClient {
  private instance: AxiosInstance

  constructor(instance: AxiosInstance) {
    this.instance = instance
  }

  /**
   * GET request helper
   */
  public async get<T>(url: string): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url)
  }

  /**
   * POST request helper
   */
  public async post<T>(url: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data)
  }

  /**
   * PUT request helper
   */
  public async put<T>(url: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data)
  }

  /**
   * PATCH request helper
   */
  public async patch<T>(url: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(url, data)
  }

  /**
   * DELETE request helper
   */
  public async delete<T>(url: string): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url)
  }

  /**
   * Get the underlying axios instance for advanced usage
   */
  public getAxiosInstance(): AxiosInstance {
    return this.instance
  }
}
